/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type ClinicProfile,
  type NotificationPrefs,
  type UpdateClinicPayload,
  clinicApi,
} from '../api/clinic.js';

const PROFILE_KEY = ['clinic', 'profile'] as const;
const NOTIFICATIONS_KEY = ['clinic', 'notifications'] as const;

export function useClinicProfile(): UseQueryResult<ClinicProfile> {
  return useQuery({ queryKey: PROFILE_KEY, queryFn: () => clinicApi.getProfile() });
}

export function useUpdateClinicProfile(): UseMutationResult<
  ClinicProfile,
  Error,
  UpdateClinicPayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateClinicPayload) => clinicApi.updateProfile(payload),
    onSuccess: (profile) => {
      qc.setQueryData(PROFILE_KEY, profile);
    },
  });
}

export function useNotificationPrefs(): UseQueryResult<NotificationPrefs> {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => clinicApi.getNotificationPrefs(),
  });
}

export function useUpdateNotificationPrefs(): UseMutationResult<
  NotificationPrefs,
  Error,
  NotificationPrefs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: NotificationPrefs) => clinicApi.updateNotificationPrefs(payload),
    onSuccess: (prefs) => {
      qc.setQueryData(NOTIFICATIONS_KEY, prefs);
    },
  });
}
